import { z } from 'zod';

export const CONFIG_CHANGES = {
  ENFORCE_TLS: 'enforceTls',
  DISABLE_PUBLIC_ACCESS: 'disablePublicAccess',
  DNS_RECORD: 'dnsRecord',
  EMAIL_AUTH: 'emailAuth',
} as const;

export const EMAIL_AUTH_MECHANISMS = {
  SPF: 'spf',
  DMARC: 'dmarc',
  DKIM: 'dkim',
} as const;

export const EmailAuthMechanismSchema = z.enum([
  EMAIL_AUTH_MECHANISMS.SPF,
  EMAIL_AUTH_MECHANISMS.DMARC,
  EMAIL_AUTH_MECHANISMS.DKIM,
]);

export type EmailAuthMechanism = z.infer<typeof EmailAuthMechanismSchema>;

const optionalNonEmptyString = z.string().min(1).optional();

export const EnforceTlsDetailsSchema = z
  .strictObject({
    listenerArn: optionalNonEmptyString,
    sslPolicy: optionalNonEmptyString,
    certificateArn: optionalNonEmptyString,
  })
  .optional();

export const DisablePublicAccessDetailsSchema = z
  .strictObject({
    bucketName: optionalNonEmptyString,
    subscriptionId: optionalNonEmptyString,
    resourceGroup: optionalNonEmptyString,
    accountName: optionalNonEmptyString,
    storageAccountName: optionalNonEmptyString,
  })
  .optional();

const DnsRecordValueFields = {
  recordType: optionalNonEmptyString,
  ttl: z.number().int().positive().optional(),
  recordValue: optionalNonEmptyString,
  recordValues: z.array(z.string().min(1)).optional(),
};

export const DnsRecordDetailsSchema = z
  .strictObject({
    hostedZoneId: optionalNonEmptyString,
    recordName: optionalNonEmptyString,
    ...DnsRecordValueFields,
  })
  .optional();

export const EmailAuthDetailsSchema = z
  .strictObject({
    mechanism: EmailAuthMechanismSchema.optional(),
    selector: optionalNonEmptyString,
    hostedZoneId: optionalNonEmptyString,
    recordName: optionalNonEmptyString,
    ...DnsRecordValueFields,
  })
  .optional();

export const EnforceTlsAgentDetailsSchema = z
  .strictObject({
    sslPolicy: optionalNonEmptyString,
  })
  .optional();

export const DisablePublicAccessAgentDetailsSchema = z
  .strictObject({})
  .optional();

export const DnsRecordAgentDetailsSchema = z
  .strictObject({
    ...DnsRecordValueFields,
  })
  .optional();

export const EmailAuthAgentDetailsSchema = z
  .strictObject({
    mechanism: EmailAuthMechanismSchema,
    selector: optionalNonEmptyString,
    ...DnsRecordValueFields,
  })
  .superRefine((details, ctx) => {
    if (
      details.mechanism === EMAIL_AUTH_MECHANISMS.DKIM &&
      !details.selector?.trim()
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'selector is required when mechanism is dkim',
        path: ['selector'],
      });
    }
  })
  .optional();

export const CONFIG_DETAILS_SCHEMAS = {
  [CONFIG_CHANGES.ENFORCE_TLS]: EnforceTlsDetailsSchema,
  [CONFIG_CHANGES.DISABLE_PUBLIC_ACCESS]: DisablePublicAccessDetailsSchema,
  [CONFIG_CHANGES.DNS_RECORD]: DnsRecordDetailsSchema,
  [CONFIG_CHANGES.EMAIL_AUTH]: EmailAuthDetailsSchema,
} as const;

export const CONFIG_AGENT_DETAILS_SCHEMAS = {
  [CONFIG_CHANGES.ENFORCE_TLS]: EnforceTlsAgentDetailsSchema,
  [CONFIG_CHANGES.DISABLE_PUBLIC_ACCESS]: DisablePublicAccessAgentDetailsSchema,
  [CONFIG_CHANGES.DNS_RECORD]: DnsRecordAgentDetailsSchema,
  [CONFIG_CHANGES.EMAIL_AUTH]: EmailAuthAgentDetailsSchema,
} as const;

export const refineConfigDetailsForChange = ({
  change,
  details,
  ctx,
  schemas,
}: {
  change: string;
  details: unknown;
  ctx: z.RefinementCtx;
  schemas: Record<string, z.ZodType>;
}): void => {
  const schema = schemas[change];

  if (!schema) {
    return;
  }

  const parsed = schema.safeParse(details);

  if (parsed.success) {
    return;
  }

  for (const issue of parsed.error.issues) {
    ctx.addIssue({
      code: 'custom',
      message: issue.message,
      path: ['details', ...issue.path],
    });
  }
};
